import { FC, useEffect, useState } from 'react';
import * as React from 'react';
import noop from 'lodash/noop';
import { useHistory } from 'react-router';
import { useAsyncFn, useAsync } from 'react-use';
import type { SelfServiceAPI } from 'types';
import useDetailsBooking from 'hooks/useDetailsBooking';
import useCancellationErrorModal from 'hooks/useCancellationErrorModal';
import { getBookingDetailsPageLink } from 'lib/links';
import acceptCancellationQuote from '../utils/acceptCancellationQuote';
import createCancellationQuote from '../utils/createCancellationQuote';
import hasExpiredCreditCardError from '../utils/hasExpiredCreditCardError';
import useExpiredCardModal from '../hooks/useExpiredCardModal';

export interface ContextValue {
  cancellationState: SelfServiceAPI.CancellationState;
  error?: Error;
  cancellation?: SelfServiceAPI.Cancellation;
  accept: () => void;
}

export const CancellationQuoteContext = React.createContext<ContextValue>({
  accept: noop,
  cancellationState: 'QUOTING',
});

const CancellationQuoteProvider: FC = ({ children }) => {
  const { booking } = useDetailsBooking();
  const { replace } = useHistory();
  const openErrorModal = useCancellationErrorModal();
  const openExpiredCreditCardModal = useExpiredCardModal();
  const [cancellation, setCancellation] = useState<SelfServiceAPI.Cancellation>();

  const createState = useAsync(async () => {
    if (!booking.flags.isCancelableInApp) {
      replace(getBookingDetailsPageLink(booking.id));
      return;
    }

    const quotedCancellation = await createCancellationQuote(booking.id);
    setCancellation(quotedCancellation);
  }, [booking.id]);

  const [acceptState, accept] = useAsyncFn(async () => {
    const acceptedCancellation = await acceptCancellationQuote(booking.id, cancellation!.id);
    setCancellation(acceptedCancellation);
  }, [booking.id, cancellation?.id]);

  const cancellationState = getCancellationState(cancellation, createState.loading, acceptState.loading);
  const error = acceptState.error || createState.error;

  // Handle cancellation state change
  useEffect(() => {
    if (error || ['FAILED', 'BLOCKED'].includes(cancellationState)) {
      if (hasExpiredCreditCardError(cancellation?.errors)) {
        openExpiredCreditCardModal();
      } else {
        openErrorModal();
      }

      return;
    }
  }, [error, cancellationState, booking.id, openErrorModal, cancellation, openExpiredCreditCardModal]);

  return (
    <CancellationQuoteContext.Provider value={{ accept, cancellation, cancellationState, error }}>
      {children}
    </CancellationQuoteContext.Provider>
  );
};

export default CancellationQuoteProvider;

export const useCancellationQuote = () => React.useContext(CancellationQuoteContext);

const getCancellationState = (
  cancellation: SelfServiceAPI.Cancellation | undefined,
  isCreateLoading: boolean,
  isAcceptLoading: boolean,
): SelfServiceAPI.CancellationState => {
  switch (true) {
    case isAcceptLoading:
      return 'ACCEPTING';
    case isCreateLoading:
    case !cancellation:
      return 'QUOTING';
    default:
      return cancellation.state;
  }
};
